'use client';

import type { ProductDetailReview } from 'src/types/product-rankings/product-rankings-ops';

import { Stack, Divider, Typography } from '@mui/material';

import { RADIUS } from 'src/theme/ui-tokens';

import { Iconify } from 'src/components/iconify';

import { Tag } from './market-rankings-badges';
import { formatCount } from '../utils/market-rankings-format';
import SectionHeading from './market-rankings-section-heading';


const STAT_SX = {
  px: 1.5,
  py: 0.6,
  borderRadius: RADIUS.md,
  bgcolor: 'grey.50',
  border: '1px solid',
  borderColor: 'divider',
};

const SUMMARY_SX = { p: 1.5, borderRadius: RADIUS.md, border: '1px solid', borderColor: 'divider' };

/**
 * 리뷰·반응 블록.
 *
 * ⚠️ 넷(별점·리뷰 수·좋아요·AI 요약) 중 **하나도 없으면 블록째 안 그린다.** 채널마다 주는
 * 것이 달라(카카오는 「찜」, 화해는 별점 위주) 빈 칸만 남는 일이 흔하다.
 *
 * ⚠️ 별점은 `4.60`이 아니라 `4.6`으로 — 뒤따르는 0을 지운다.
 */
export default function ReviewBlock({
  review,
  source,
}: {
  review: ProductDetailReview | null | undefined;
  source?: string;
}) {
  const avg = review?.avg ?? 0;
  const count = review?.count ?? 0;
  const likeCount = review?.likeCount ?? 0;
  const hasRating = avg > 0;
  const hasCount = count > 0;
  const hasLike = likeCount > 0;
  const hasSentiment = Boolean(review?.positive || review?.negative);

  if (!review || (!hasRating && !hasCount && !hasLike && !hasSentiment)) return null;

  return (
    <>
      <Divider sx={{ my: 2 }} />
      <SectionHeading icon="solar:star-linear" iconColor="warning.main" title="리뷰·반응" />
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: hasSentiment ? 1.5 : 0 }}>
        {hasRating ? (
          <Stack direction="row" spacing={0.5} alignItems="center" sx={STAT_SX}>
            <Iconify icon="solar:star-linear" width={15} sx={{ color: 'warning.main' }} />
            <Typography sx={{ fontWeight: 600, typography: 'uiBody' }}>
              {avg.toFixed(2).replace(/\.?0+$/, '')}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.disabled' }}>/ 5</Typography>
          </Stack>
        ) : null}
        {hasCount ? (
          <Stack direction="row" spacing={0.5} alignItems="center" sx={STAT_SX}>
            <Iconify icon="solar:chat-round-line-linear" width={15} sx={{ color: 'text.secondary' }} />
            <Typography sx={{ fontWeight: 600, typography: 'uiBody' }}>{formatCount(count)}</Typography>
            <Typography variant="caption" sx={{ color: 'text.disabled' }}>리뷰</Typography>
          </Stack>
        ) : null}
        {hasLike ? (
          <Stack direction="row" spacing={0.5} alignItems="center" sx={STAT_SX}>
            <Iconify icon="solar:heart-linear" width={15} sx={{ color: 'error.main' }} />
            <Typography sx={{ fontWeight: 600, typography: 'uiBody' }}>{formatCount(likeCount)}</Typography>
            {/* ⚠️ 카카오선물하기만 「찜」이다 */}
            <Typography variant="caption" sx={{ color: 'text.disabled' }}>
              {source === 'kakaogift' ? '찜' : '좋아요'}
            </Typography>
          </Stack>
        ) : null}
      </Stack>

      {review.keywords?.length ? (
        <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0.5} sx={{ mb: 1.5 }}>
          {review.keywords.map((keyword) => (
            <Tag key={keyword} sx={{ bgcolor: 'grey.100', color: 'text.secondary' }}>#{keyword}</Tag>
          ))}
        </Stack>
      ) : null}

      {hasSentiment ? (
        <Stack spacing={1}>
          {review.positive ? (
            <Stack direction="row" spacing={1} sx={{ ...SUMMARY_SX, bgcolor: 'success.lighter' }}>
              <Iconify icon="solar:like-linear" width={16} sx={{ color: 'success.dark', mt: 0.2, flexShrink: 0 }} />
              <Typography sx={{ typography: 'uiMeta', lineHeight: 1.5 }}>{review.positive}</Typography>
            </Stack>
          ) : null}
          {review.negative ? (
            <Stack direction="row" spacing={1} sx={{ ...SUMMARY_SX, bgcolor: 'warning.lighter' }}>
              <Iconify icon="solar:dislike-linear" width={16} sx={{ color: 'warning.dark', mt: 0.2, flexShrink: 0 }} />
              <Typography sx={{ typography: 'uiMeta', lineHeight: 1.5 }}>{review.negative}</Typography>
            </Stack>
          ) : null}
          <Typography variant="caption" sx={{ color: 'text.disabled' }}>
            리뷰 요약은 AI가 실제 리뷰를 읽고 정리한 내용입니다.
          </Typography>
        </Stack>
      ) : null}
    </>
  );
}
